import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {
  EventEmitter,
  Injectable
}                                                                 from '@angular/core';
import {
  filter,
  map,
  skip
}                                                                 from 'rxjs/operators';
import {
  ObjectFactory
}                                                                 from 'ts-tooling';
import {
  FwPlanningWizardStates,
  IFwPlanPlanningWizardDetailsData,
  IFwPlanPlanningWizardOverviewData,
  IFwPlanPlanningWizardOverviewProvider,
  IFwPlanPlanningWizardProvider,
  IFwPlanPlanningWizardSelectionData,
  IFwPlanPlanningWizardSingleEditData
}                                                                 from '../fw-plan-planning.types';
import {
  ApDynGridColumnConfigBase
}                                                                 from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                 from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynformsConfigFieldset
}                                                                 from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynGridPagerWizardBreadcrumbsStepper,
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig
}                                                                 from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApDynComponentComponent
}                                                                 from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  FieldStore
}                                                                 from '../../../../stores/farm/field.store';
import {
  LanguageStore
}                                                                 from '../../../../stores/translation/language.store';
import {
  SettingsStore
}                                                                 from '../../../../stores/base-data/settings.store';
import {
  InstrumentStore
}                                                                 from '../../../../stores/docu/instrument.store';
import {
  MachineStore
}                                                                 from '../../../../stores/docu/machine.store';
import {
  DriverStore
}                                                                 from '../../../../stores/docu/driver.store';
import {
  ApTranslationService
}                                                                 from '../../../../ap-utils/service/ap-translation.service';
import {
  IModalDialogData,
  ModalDialogButtonDisable
}                                                                 from '../../../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {
  ApUtilService
}                                                                 from '../../../../ap-utils/service/ap-util.service';
import {
  ModalDialogStore
}                                                                 from '../../../../stores/dialog/modal.dialog.store';
import {
  FwPlanPlanningOverviewConfig
}                                                                 from '../configs/fw-plan-planning-overview.config';

@Injectable()
export class FwPlanPlanningOverviewProvider implements IFwPlanPlanningWizardOverviewProvider,
  IFwPlanPlanningWizardProvider<IFwPlanPlanningWizardOverviewData, IFwPlanPlanningWizardOverviewData[]> {

  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;
  private _handleStateChange: EventEmitter<FwPlanningWizardStates>;
  private _dynComponent: ApDynComponentComponent;
  private _items$: BehaviorSubject<IFwPlanPlanningWizardOverviewData[]>;
  private _subscriptions: Subscription[] = [];

  constructor(private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private instrumentStore: InstrumentStore,
              private modalDialogStore: ModalDialogStore,
              private translationService: ApTranslationService,
              private fwPlanPlanningWizardOverviewConfig: FwPlanPlanningOverviewConfig) {
  }

  /**
   * Initializes the provider with required observables and components
   * @param caption$ - BehaviorSubject for managing caption text
   * @param fieldSets$ - BehaviorSubject for managing field sets
   * @param handleStateChange - EventEmitter for handling wizard state changes
   * @param dynComponent - Reference to the dynamic component
   * @public
   */
  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              handleStateChange: EventEmitter<FwPlanningWizardStates>,
              dynComponent: ApDynComponentComponent): void {
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._handleStateChange = handleStateChange;
    this._dynComponent = dynComponent;
    this._items$ = new BehaviorSubject<IFwPlanPlanningWizardOverviewData[]>([]);
    this._subscriptions = [];
  }

  /**
   * Builds data items for the overview page by combining selection and details data
   * @param selectionData The selected fields data from selection step
   * @param detailsData The entered details data from details step
   * @returns Observable of overview data items for the grid
   * @public
   */
  public buildDataItems(selectionData: IFwPlanPlanningWizardSelectionData[],
                        detailsData: IFwPlanPlanningWizardDetailsData): Observable<IFwPlanPlanningWizardOverviewData[]> {
    const defaultGridItems = this.generateDefaultGridItems(selectionData, detailsData);
    this._items$.next(defaultGridItems);
    return combineLatest([
      this._items$.asObservable()
    ]).pipe(
      map(([gridItems]) => gridItems.map((gridItem) => gridItem)
      )
    );
  }

  /**
   * Handles grid selection changes by updating selected fields in the field store
   * @param items - Array of selected overview data items
   * @public
   */
  public onGridSelectionChanged(items: IFwPlanPlanningWizardOverviewData[]): void {
    this.fieldStore.changeSelectedField(items.map((item) => item?.Id?.toString()));
  }

  /**
   * Cleans up subscriptions when the provider is destroyed
   * @public
   */
  public onDestroy(): void {
    this._subscriptions.forEach(s => s?.unsubscribe());
  }

  /**
   * Handles leaving the overview provider by cleaning up subscriptions and returning a copy of current items
   * @returns Copy of current overview data items
   * @public
   */
  public onLeave(): IFwPlanPlanningWizardOverviewData[] {
    this._subscriptions.forEach(s => s?.unsubscribe());
    return ObjectFactory.Copy(this._items$.getValue());
  }

  /**
   * Updates the caption text to display 'Admin_Pages__Overview'
   * @public
   */
  public updateCaption(): void {
    this._caption$.next('Admin_Pages__Overview');
  }

  /**
   * Updates field sets by clearing them
   * This is called when no field sets are needed for the current view
   * @public
   */
  public updateFieldSets(): void {
    this._fieldSets$.next([]);
  }

  /**
   * Gets grid column configuration by combining language and settings data
   * @param _detailsData The details data (not used in this method)
   * @returns Observable of a grid column configuration array
   * @public
   */
  public getGridColumnConfig(_detailsData: IFwPlanPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, setting]) => !!language && !!setting),
      map(([language, setting]) => {
        const dropDownData = [
          {
            text: 'Global__Edit',
            callback: ApUtilService.createEventEmitter(this.onEdit.bind(this), this._subscriptions)
          }
        ];
        return this.fwPlanPlanningWizardOverviewConfig.generateColumns(language, setting, dropDownData);
      })
    );
  }

  /**
   * Gets pager configuration with back/next buttons and breadcrumbs stepper
   * @param breadCrumbsColumns Array of wizard states to show in breadcrumbs
   * @returns Configured pager for the overview page with back, breadcrumbs and save buttons
   * @public
   */
  public getPagerConfig(breadCrumbsColumns: FwPlanningWizardStates[]): ApDynGridPagerConfigBase {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global_Back_With_Arrow', this._handleStateChange,
        {value: FwPlanningWizardStates.Details}),
      new ApDynGridPagerWizardBreadcrumbsStepper(breadCrumbsColumns, 2),
      new ApDynGridPagerWizardButton('Global__Save', this._handleStateChange,
        {active: this._dynComponent.Valid$, value: FwPlanningWizardStates.Save, primary: true})
    );
  }

  /**
   * Generates default grid items for the overview by transforming selection and details data
   * @param selectionData Array of selected field data from selection step
   * @param detailsData Details data entered in details step
   * @returns Array of overview data items containing field, work details and resource information
   * @private
   */
  private generateDefaultGridItems(selectionData: IFwPlanPlanningWizardSelectionData[],
                                   detailsData: IFwPlanPlanningWizardDetailsData): IFwPlanPlanningWizardOverviewData[] {
    const getPlanBooks = this.fwPlanPlanningWizardOverviewConfig.getPlanBooksForSaving(detailsData, selectionData);
    return selectionData.map(selectionItem => {
      const planBook = getPlanBooks.find(p => p.FieldGeomId === selectionItem?.FieldGeomId);
      return {
        Id: selectionItem.Id,
        ApplicationDate: planBook.ApplicationDate,
        Value: planBook.Value,
        Notes: planBook.Note,
        Unit: this.translationService.translate(planBook.Unit?.Key),
        WorkType: this.translationService.translate(planBook.WorkType?.Description),
        Start: detailsData.Start,
        DurationInMilliseconds: planBook.Duration?.TotalMilliseconds,
        HaPerHour: detailsData.HaPerHour,
        SelectionData: selectionItem,
        DetailsData: detailsData,
        PlanBook: planBook,
        Machine: this.machineStore.getMachineById(detailsData.Machine)?.NameWithSign,
        Instrument: this.instrumentStore.getInstrumentById(detailsData.Instrument)?.Name,
        Driver: this.driverStore.getDriverById(detailsData.Driver)?.FirstNameFullName
      } as IFwPlanPlanningWizardOverviewData;
    });
  }

  /**
   * Handles editing of an overview item by showing a modal dialog with edit form
   * @param overviewItem The overview item to be edited
   * @private
   */
  private onEdit(overviewItem: IFwPlanPlanningWizardOverviewData): void {
    const singleEditData = this.fwPlanPlanningWizardOverviewConfig.getSingleEditData(overviewItem);
    const dialog = {
      title: this.translationService.translate('Global__Edit'),
      show: true,
      buttons: [
        {
          key: 'cancel',
          text: 'Global__Cancel',
          primary: false
        },
        {
          key: 'apply',
          text: 'Global__Ok',
          primary: true,
          disable: ModalDialogButtonDisable.FormInvalid,
        }
      ],
      formConfig: {
        fieldSets: this.fwPlanPlanningWizardOverviewConfig.generateModalDialogFields(overviewItem,
          singleEditData, this.modalDialogStore.listenChanges()),
        validators: undefined,
        asyncValidators: undefined,
        values: ObjectFactory.Copy(singleEditData)
      }
    } as IModalDialogData;
    const resultSubscription = this.modalDialogStore.Listen(s => s.result)
      .pipe(skip(1))
      .subscribe((result) => {
        switch (result?.key) {
          case 'apply': {
            const singleSelectionData = result.formValues as IFwPlanPlanningWizardSingleEditData;
            this._items$.next(
              this._items$.getValue().map((item: IFwPlanPlanningWizardOverviewData) => {
                if (item.Id === overviewItem.Id) {
                  return this.fwPlanPlanningWizardOverviewConfig.updateSingleOverviewItem(item, singleSelectionData);
                }
                return item;
              })
            );
            resultSubscription.unsubscribe();
            break;
          }
          case 'cancel': {
            resultSubscription.unsubscribe();
            break;
          }
        }
      });
    this.modalDialogStore.setModalDialogData(dialog);
  }
}
